import React from 'react';
import {useIntl} from 'react-intl';
import styles from './config-monitoring.module.scss';
import clsx from 'clsx';
import ReactSelect, {components, OptionProps} from 'react-select';
import {PageTitle} from '../../../../../_metronic/layout/core';
import {Card, Col, Row, OverlayTrigger, Tooltip, Spinner, Form} from 'react-bootstrap';
import Level3Title from '../../../../common/components/title/Level3Title';
import {useGetMonitoring} from '../../../../../Domains/Service/Hooks/MonitoringManager';
import {BuMonitoring, EtatLien, LinkMonitoring} from '../../../../../Domains/Service/Types/service';
import {GraphServiceType} from '../../../../../Domains/Service/Types/graphPeriods';
import GraphPopoverContent from './GraphPopoverContent';

type MonitoringFilter = 'all' | 'connecte' | 'deconnecte' | 'a-configurer';

interface AccountOption {
  value: string;
  label: string;
  count: number;
}

interface SiteOption {
  value: string;
  label: string;
  count: number;
}

const filterSelectStyles = {
  menuPortal: (base: Record<string, unknown>) => ({...base, zIndex: 9999}),
};

const ConfigMonitoring = (): JSX.Element => {
  const intl = useIntl();
  const [openTooltipId, setOpenTooltipId] = React.useState<string | null>(null);
  const [search, setSearch] = React.useState('');
  const [statusFilter, setStatusFilter] = React.useState<MonitoringFilter>('all');
  const [selectedAccounts, setSelectedAccounts] = React.useState<AccountOption[]>([]);
  const [selectedSites, setSelectedSites] = React.useState<SiteOption[]>([]);
  const {data: performanceData, isLoading, isError} = useGetMonitoring();

  const accountOptions = React.useMemo<AccountOption[]>(() => {
    if (!performanceData) return [];
    return performanceData
      .map((bu) => ({
        value: bu.id,
        label: [bu.id, bu.name].filter(Boolean).join(' - '),
        count: bu.sites.reduce((acc, s) => acc + s.liens.length, 0),
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [performanceData]);

  const siteOptions = React.useMemo<SiteOption[]>(() => {
    if (!performanceData) return [];
    const map = new Map<string, SiteOption>();
    performanceData.forEach((bu) => {
      bu.sites.forEach((site) => {
        const existing = map.get(site.id);
        if (existing) {
          existing.count += site.liens.length;
          return;
        }
        map.set(site.id, {
          value: site.id,
          label: [site.id, site.name].filter(Boolean).join(' - '),
          count: site.liens.length,
        });
      });
    });
    return Array.from(map.values()).sort((a, b) => a.label.localeCompare(b.label));
  }, [performanceData]);

  // Filtre par comptes (BU) et sites sélectionnés, en amont des autres filtres
  const baseFilteredData = React.useMemo<BuMonitoring[]>(() => {
    if (!performanceData) return [];
    const accountIds = new Set(selectedAccounts.map((o) => o.value));
    const siteIds = new Set(selectedSites.map((o) => o.value));
    if (accountIds.size === 0 && siteIds.size === 0) return performanceData;

    return performanceData
      .filter((bu) => accountIds.size === 0 || accountIds.has(bu.id))
      .map((bu) => {
        const filteredSites =
          siteIds.size === 0 ? bu.sites : bu.sites.filter((site) => siteIds.has(site.id));
        return filteredSites.length > 0 ? {...bu, sites: filteredSites} : null;
      })
      .filter((bu): bu is BuMonitoring => bu !== null);
  }, [performanceData, selectedAccounts, selectedSites]);

  const counts = React.useMemo(() => {
    const all = baseFilteredData.flatMap((bu) => bu.sites.flatMap((s) => s.liens));
    return {
      all: all.length,
      connecte: all.filter((l) => l.etat === 'CONNECTED').length,
      deconnecte: all.filter((l) => l.etat === 'DISCONNECTED').length,
      aConfigurer: all.filter((l) => l.etat === 'TO_CONFIGURE').length,
    };
  }, [baseFilteredData]);

  const filteredData = React.useMemo<BuMonitoring[]>(() => {
    const q = search.trim().toLowerCase();

    const matches = (s: string | null | undefined) => !!s && s.toLowerCase().includes(q);
    const linkMatchesStatus = (link: LinkMonitoring) => {
      if (statusFilter === 'all') return true;
      if (statusFilter === 'connecte') return link.etat === 'CONNECTED';
      if (statusFilter === 'a-configurer') return link.etat === 'TO_CONFIGURE';
      return link.etat === 'DISCONNECTED';
    };
    const linkMatchesSearch = (link: LinkMonitoring) =>
      q === '' || matches(link.name) || matches(link.id) || matches(link.ip);

    return baseFilteredData
      .map((bu) => {
        const buNameMatches = q !== '' && matches(bu.name);
        const filteredSites = bu.sites
          .map((site) => {
            const siteNameMatches = q !== '' && matches(site.name);
            const filteredLiens = site.liens.filter((link) => {
              if (!linkMatchesStatus(link)) return false;
              if (buNameMatches || siteNameMatches) return true;
              return linkMatchesSearch(link);
            });
            return filteredLiens.length > 0 ? {...site, liens: filteredLiens} : null;
          })
          .filter((s): s is typeof bu.sites[number] => s !== null);
        return filteredSites.length > 0 ? {...bu, sites: filteredSites} : null;
      })
      .filter((bu): bu is BuMonitoring => bu !== null);
  }, [baseFilteredData, search, statusFilter]);

  const filterButtons: {
    key: MonitoringFilter;
    label: string;
    count: number;
    color: string;
    icon: string;
  }[] = [
    {
      key: 'all',
      label: intl.formatMessage({id: 'SERVICES.CONNEXION.ALL'}),
      count: counts.all,
      color: '#7E8299',
      icon: 'bi-list-ul',
    },
    {
      key: 'connecte',
      label: intl.formatMessage({id: 'LIEN.DETAIL.STATE.CONNECTED'}),
      count: counts.connecte,
      color: '#50cd89',
      icon: 'bi-check-circle',
    },
    {
      key: 'deconnecte',
      label: intl.formatMessage({id: 'LIEN.DETAIL.STATE.DISCONNECTED'}),
      count: counts.deconnecte,
      color: '#f1416c',
      icon: 'bi-x-circle',
    },
    {
      key: 'a-configurer',
      label: intl.formatMessage({id: 'LIEN.DETAIL.STATE.TO_CONFIGURE'}),
      count: counts.aConfigurer,
      color: '#F1A340',
      icon: 'bi-gear',
    },
  ];

  const renderGraphTooltip = (title: string, buId: string, serviceType: GraphServiceType) => (
    <Tooltip id="graph-tooltip" className={styles.graphTooltip}>
      <GraphPopoverContent
        buId={buId}
        serviceType={serviceType}
        title={title}
        onClose={() => setOpenTooltipId(null)}
      />
    </Tooltip>
  );

  const renderEtatLien = (etat: EtatLien): JSX.Element => {
    if (etat === 'TO_CONFIGURE') {
      return (
        <span className={clsx(styles.statusBadge, styles.statCardGray)}>
          {intl.formatMessage({id: 'LIEN.DETAIL.STATE.TO_CONFIGURE'})}
        </span>
      );
    }
    if (etat === 'CONNECTED') {
      return (
        <span className={clsx(styles.statusBadge, styles.statCardGreen)}>
          {intl.formatMessage({id: 'LIEN.DETAIL.STATE.CONNECTED'})}
        </span>
      );
    }
    return (
      <span className={clsx(styles.statusBadge, styles.statCardRed)}>
        {intl.formatMessage({id: 'LIEN.DETAIL.STATE.DISCONNECTED'})}
      </span>
    );
  };

  const renderGraphButton = (
    tooltipKey: string,
    title: string,
    buId: string,
    serviceType: GraphServiceType,
    label?: string
  ): JSX.Element => (
    <OverlayTrigger
      placement="left"
      overlay={renderGraphTooltip(title, buId, serviceType)}
      show={openTooltipId === tooltipKey}
      trigger="click"
      onToggle={(nextShow) => {
        if (nextShow) setOpenTooltipId(tooltipKey);
      }}
    >
      <button className={clsx('btn btn-sm btn-light-primary', label && styles.mobileActionButton)}>
        <i className={clsx('fa fa-signal', label && 'me-2')}></i>
        {label}
      </button>
    </OverlayTrigger>
  );

  const renderLinkRow = (link: LinkMonitoring, buId: string): JSX.Element => (
    <tr key={link.id}>
      <td className={styles.linkCell}>
        <span className={styles.linkId}>{link.id}</span>
        {link.name}
      </td>
      <td className={styles.centeredCell}>{link.debit}</td>
      <td className={styles.centeredCell}>{link.ip ?? ''}</td>
      <td className={styles.centeredCell}>{renderEtatLien(link.etat)}</td>
      <td className={styles.centeredCell}>
        {link.latence_ping ?? <span className={styles.dashCell}>-</span>}
      </td>
      <td className={styles.centeredCell}>
        {link.ping_graph &&
          renderGraphButton(
            `${link.id}-ping`,
            intl.formatMessage({id: 'MONITORING.GRAPH.LATENCY_TITLE'}),
            link.id,
            'ping-link'
          )}
      </td>
      <td className={link.monitorable ? styles.centeredCell : styles.nonMonitorableCell}>
        {link.monitorable
          ? link.trafic_entrant ?? '-'
          : intl.formatMessage({id: 'MONITORING.NOT_MONITORABLE'})}
      </td>
      <td className={link.monitorable ? styles.centeredCell : styles.nonMonitorableCell}>
        {link.monitorable
          ? link.trafic_sortant ?? '-'
          : intl.formatMessage({id: 'MONITORING.NOT_MONITORABLE'})}
      </td>
      <td className={styles.centeredCell}>
        {link.trafic_graph &&
          renderGraphButton(
            `${link.id}-trafic`,
            intl.formatMessage({id: 'MONITORING.GRAPH.TRAFFIC_TITLE'}),
            link.id,
            'trafic'
          )}
      </td>
    </tr>
  );

  const renderLinkCard = (link: LinkMonitoring): JSX.Element => (
    <div className={styles.mobileLinkCard} key={link.id}>
      <div className={styles.mobileCardHeader}>
        <div>
          <span className={styles.linkId}>{link.id}</span>
          <span className="fw-bold text-dark">{link.name}</span>
        </div>
        {renderEtatLien(link.etat)}
      </div>
      <div className={styles.mobileCardRow}>
        <span className={styles.mobileCardLabel}>
          {intl.formatMessage({id: 'MONITORING.COL.SPEED'})}
        </span>
        <span>{link.debit}</span>
      </div>
      <div className={styles.mobileCardRow}>
        <span className={styles.mobileCardLabel}>
          {intl.formatMessage({id: 'MONITORING.COL.IP'})}
        </span>
        <span>{link.ip ?? '-'}</span>
      </div>
      <div className={styles.mobileCardRow}>
        <span className={styles.mobileCardLabel}>
          {intl.formatMessage({id: 'MONITORING.COL.PING_LATENCY'})}
        </span>
        <span>{link.latence_ping ?? '-'}</span>
      </div>
      <div className={styles.mobileCardRow}>
        <span className={styles.mobileCardLabel}>
          {intl.formatMessage({id: 'MONITORING.COL.INBOUND'})}
        </span>
        <span>
          {link.monitorable
            ? link.trafic_entrant ?? '-'
            : intl.formatMessage({id: 'MONITORING.NOT_MONITORABLE'})}
        </span>
      </div>
      <div className={styles.mobileCardRow}>
        <span className={styles.mobileCardLabel}>
          {intl.formatMessage({id: 'MONITORING.COL.OUTBOUND'})}
        </span>
        <span>
          {link.monitorable
            ? link.trafic_sortant ?? '-'
            : intl.formatMessage({id: 'MONITORING.NOT_MONITORABLE'})}
        </span>
      </div>
      {(link.ping_graph || link.trafic_graph) && (
        <div className={styles.mobileCardActions}>
          {link.ping_graph &&
            renderGraphButton(
              `${link.id}-ping`,
              intl.formatMessage({id: 'MONITORING.GRAPH.LATENCY_TITLE'}),
              link.id,
              'ping-link',
              intl.formatMessage({id: 'MONITORING.COL.PING_GRAPH'})
            )}
          {link.trafic_graph &&
            renderGraphButton(
              `${link.id}-trafic`,
              intl.formatMessage({id: 'MONITORING.GRAPH.TRAFFIC_TITLE'}),
              link.id,
              'trafic',
              intl.formatMessage({id: 'MONITORING.COL.TRAFFIC_GRAPH'})
            )}
        </div>
      )}
    </div>
  );

  const CheckboxOption = <T extends {label: string; count?: number}>(
    props: OptionProps<T, true>
  ) => (
    <components.Option {...props}>
      <div className="d-flex align-items-start">
        <input
          type="checkbox"
          checked={props.isSelected}
          onChange={() => null}
          className="me-2 mt-1 flex-shrink-0"
        />
        <label className="mb-0 flex-grow-1" style={{wordBreak: 'break-word'}}>
          {props.label}
        </label>
        {props.data.count != null && (
          <span className="ms-2 text-muted flex-shrink-0">({props.data.count})</span>
        )}
      </div>
    </components.Option>
  );

  return (
    <>
      <PageTitle>{intl.formatMessage({id: 'CONFIGURATION.TEL.CONF'})}</PageTitle>

      <div className={clsx(styles.containerConfigMonitoring)}>
        <Row className="mt-4 mb-6">
          <Col md={12}>
            <div className="d-flex gap-3 mb-3 flex-wrap">
              {filterButtons.map((btn) => {
                return (
                  <div
                    key={btn.key}
                    role="button"
                    onClick={() => setStatusFilter(btn.key)}
                    className="d-flex align-items-center bg-white rounded px-4 py-3 flex-grow-1"
                    style={{minWidth: 0, cursor: 'pointer', border: '1px solid #e6e6e6'}}
                  >
                    <span
                      className="rounded-circle me-3"
                      style={{
                        width: 8,
                        height: 8,
                        backgroundColor: btn.color,
                        flexShrink: 0,
                      }}
                    />
                    <div className="d-flex flex-column">
                      <span className="fw-bold fs-3 lh-1">{btn.count}</span>
                      <span className="text-muted small mt-1">{btn.label}</span>
                    </div>
                    <i
                      className={`bi ${btn.icon} ms-auto ps-3 fs-2`}
                      style={{color: btn.color, flexShrink: 0}}
                    />
                  </div>
                );
              })}
            </div>
            <Card>
              <Card.Header className="border-0 pt-6 d-block">
                <div className="card-title mb-4">
                  <Level3Title title={intl.formatMessage({id: 'MONITORING.TITLE'})} />
                </div>
                <div className="d-flex flex-wrap align-items-center gap-5">
                  <div className={styles.filterSelect}>
                    <ReactSelect<AccountOption, true>
                      isMulti
                      closeMenuOnSelect={false}
                      hideSelectedOptions={false}
                      components={{Option: CheckboxOption}}
                      options={accountOptions}
                      value={selectedAccounts}
                      onChange={(vals) => setSelectedAccounts(vals ? [...vals] : [])}
                      placeholder={intl.formatMessage({id: 'SERVICES.FILTER.ALL_ACCOUNTS'})}
                      noOptionsMessage={() => intl.formatMessage({id: 'FIELD.NO.OPTIONS'})}
                      classNamePrefix="filter-select"
                      menuPortalTarget={document.body}
                      styles={filterSelectStyles}
                    />
                  </div>
                  <div className={styles.filterSelectSite}>
                    <ReactSelect<SiteOption, true>
                      isMulti
                      closeMenuOnSelect={false}
                      hideSelectedOptions={false}
                      components={{Option: CheckboxOption}}
                      options={siteOptions}
                      value={selectedSites}
                      onChange={(vals) => setSelectedSites(vals ? [...vals] : [])}
                      placeholder={intl.formatMessage({id: 'SERVICES.FILTER.ALL_SITES'})}
                      noOptionsMessage={() => intl.formatMessage({id: 'FIELD.NO.OPTIONS'})}
                      classNamePrefix="filter-select"
                      menuPortalTarget={document.body}
                      styles={filterSelectStyles}
                    />
                  </div>
                  <div className={styles.searchWrapper}>
                    <Form.Control
                      type="search"
                      size="sm"
                      placeholder={intl.formatMessage({id: 'MONITORING.SEARCH.PLACEHOLDER'})}
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      className={styles.searchInput}
                    />
                  </div>
                </div>
              </Card.Header>
              <Card.Body>
                {isLoading && (
                  <div className="text-center p-4">
                    <Spinner animation="border" />
                  </div>
                )}
                {isError && (
                  <div className="text-center p-4 text-danger">
                    {intl.formatMessage({id: 'MONITORING.ERROR'})}
                  </div>
                )}
                {!isLoading && !isError && performanceData && (
                  <>
                    <div className="d-none d-md-block">
                      <table className={styles.performanceTable}>
                        <thead>
                          <tr className={styles.headerRow}>
                            <th>{intl.formatMessage({id: 'MONITORING.COL.LINK'})}</th>
                            <th>{intl.formatMessage({id: 'MONITORING.COL.SPEED'})}</th>
                            <th>{intl.formatMessage({id: 'MONITORING.COL.IP'})}</th>
                            <th>{intl.formatMessage({id: 'MONITORING.COL.LINK_STATE'})}</th>
                            <th>{intl.formatMessage({id: 'MONITORING.COL.PING_LATENCY'})}</th>
                            <th>{intl.formatMessage({id: 'MONITORING.COL.PING_GRAPH'})}</th>
                            <th>{intl.formatMessage({id: 'MONITORING.COL.INBOUND'})}</th>
                            <th>{intl.formatMessage({id: 'MONITORING.COL.OUTBOUND'})}</th>
                            <th>{intl.formatMessage({id: 'MONITORING.COL.TRAFFIC_GRAPH'})}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredData.length === 0 && (
                            <tr>
                              <td colSpan={9} className="text-center text-muted p-4">
                                {intl.formatMessage({id: 'MONITORING.EMPTY'})}
                              </td>
                            </tr>
                          )}
                          {filteredData.map((bu) => (
                            <React.Fragment key={bu.name}>
                              <tr>
                                <td colSpan={9} className={styles.headerBU}>
                                  {bu.name}
                                </td>
                              </tr>
                              {bu.sites.map((site) => (
                                <React.Fragment key={site.id}>
                                  <tr>
                                    <td colSpan={9} className={styles.headerService}>
                                      {intl.formatMessage({id: 'MONITORING.SITE_PREFIX'})} {site.id}{' '}
                                      {site.name}
                                    </td>
                                  </tr>
                                  {site.liens.map((link) => renderLinkRow(link, bu.id))}
                                </React.Fragment>
                              ))}
                            </React.Fragment>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    <div className="d-md-none">
                      {filteredData.length === 0 && (
                        <div className="text-center text-muted p-4">
                          {intl.formatMessage({id: 'MONITORING.EMPTY'})}
                        </div>
                      )}
                      {filteredData.map((bu) => (
                        <div key={bu.name}>
                          <div className={styles.mobileGroupBu}>{bu.name}</div>
                          {bu.sites.map((site) => (
                            <div key={site.id}>
                              <div className={styles.mobileGroupSite}>
                                {intl.formatMessage({id: 'MONITORING.SITE_PREFIX'})} {site.id}{' '}
                                {site.name}
                              </div>
                              {site.liens.map((link) => renderLinkCard(link))}
                            </div>
                          ))}
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </div>
    </>
  );
};

export default ConfigMonitoring;
